import React, { useState, useEffect } from "react";

const OnboardPatientForm = ({
  initialValues,
  doctors,
  devices,
  onSubmit,
  onCancel,
  isEditMode = false,
  error,
  onClearError,
}) => {
  const [patientHospitalId, setPatientHospitalId] = useState(
    initialValues?.patient_hospital_id || "",
  );
  const [bedNumber, setBedNumber] = useState(initialValues?.bed_number || "");
  const [wardName, setWardName] = useState(initialValues?.ward_name || "");
  const [careTakerName, setCareTakerName] = useState(
    initialValues?.caretaker_name || "",
  );
  const [careTakerEmail, setCareTakerEmail] = useState(
    initialValues?.caretaker_email || "",
  );
  const [careTakerMobile, setCareTakerMobile] = useState(
    initialValues?.caretaker_mobile || "",
  );
  const [name, setName] = useState(initialValues?.name || "");
  const [dateOfBirth, setDateOfBirth] = useState(
    initialValues?.date_of_birth || "",
  );
  const [age, setAge] = useState(initialValues?.age || "");
  const [gender, setGender] = useState(initialValues?.gender || "");
  const [admittedOn, setAdmittedOn] = useState(
    initialValues?.admitted_on || new Date().toISOString().split("T")[0],
  );
  const [isReleased, setIsReleased] = useState(
    initialValues?.is_released || false,
  );
  const [isActive, setIsActive] = useState(
    initialValues?.is_active !== undefined ? initialValues.is_active : true,
  );
  const [deviceId, setDeviceId] = useState(initialValues?.device_id || "");
  const [doctorId, setDoctorId] = useState(initialValues?.doctor_id || "");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const calculateAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age.toString();
  };

  useEffect(() => {
    if (dateOfBirth) {
      setAge(calculateAge(dateOfBirth));
    } else {
      setAge("");
    }
  }, [dateOfBirth]);

  useEffect(() => {
    if (initialValues) {
      console.log("Setting initial values:", initialValues);
      setPatientHospitalId(initialValues.patient_hospital_id || "");
      setBedNumber(initialValues.bed_number || "");
      setWardName(initialValues.ward_name || "");
      setAge(initialValues.age || "");

      setCareTakerName(initialValues.caretaker_name || "");
      setCareTakerEmail(initialValues.caretaker_email || "");
      setCareTakerMobile(initialValues.caretaker_mobile || "");
      setName(initialValues.name || "");
      setDateOfBirth(initialValues.date_of_birth || "");
      setGender(initialValues.gender || "");
      setAdmittedOn(
        initialValues.admitted_on || new Date().toISOString().split("T")[0],
      );
      setIsReleased(initialValues.is_released || false);
      setIsActive(
        initialValues.is_active !== undefined ? initialValues.is_active : true,
      );
      setDeviceId(initialValues.device_id || "");
      setDoctorId(initialValues.doctor_id || "");
    }
  }, [initialValues]);

  const validateForm = () => {
    let tempErrors = {};
    if (!patientHospitalId)
      tempErrors.patient_hospital_id = "Patient Hospital ID is required";
    if (!bedNumber) tempErrors.bed_number = "Bed Number is required";
    if (!wardName) tempErrors.ward_name = "Ward Name is required";
    // if (!careTakerName)
    //   tempErrors.caretaker_name = "Caretaker Name is required";
    // if (!careTakerEmail)
    //   tempErrors.caretaker_email = "Caretaker Email is required";
    // if (!careTakerMobile)
    //   tempErrors.caretaker_mobile = "Caretaker Mobile is required";
    if (!name) tempErrors.name = "Patient Name is required";
    // if (!dateOfBirth) tempErrors.date_of_birth = "Date of Birth is required";
    // else if (isNaN(new Date(dateOfBirth).getTime()))
    //   tempErrors.date_of_birth = "Invalid Date of Birth";
    if (!age) tempErrors.age = "Age is required";
    if (!gender) tempErrors.gender = "Gender is required";
    if (!deviceId) tempErrors.device_id = "Device is required";
    if (!doctorId) tempErrors.doctor_id = "Doctor is required";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const patientData = {
        patient_hospital_id: patientHospitalId,
        bed_number: bedNumber,
        ward_name: wardName,
        // caretaker_name: careTakerName,
        // caretaker_email: careTakerEmail,
        // caretaker_mobile: careTakerMobile,

        name,
        // date_of_birth: dateOfBirth,
        age,
        gender,
        admitted_on: admittedOn,
        is_released: isReleased,
        is_active: isActive,
        device_id: deviceId,
        doctor_id: doctorId,
      };

      await onSubmit(patientData);
    } catch (err) {
      setErrors({ form: err.message || "Failed to submit the form." });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  const handleClearFormError = () => {
    setErrors({});
    if (onClearError) onClearError();
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6 space-y-6">
        {(errors.form || error) && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg flex justify-between items-center">
            <span>{errors.form || error}</span>
          </div>
        )}
        <div className="bg-gray-50 p-6 rounded-lg space-y-6">
          <h3 className="text-lg font-semibold text-gray-800">
            Patient Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Patient Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.name ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Patient Hospital ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={patientHospitalId}
                onChange={(e) => setPatientHospitalId(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.patient_hospital_id ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.patient_hospital_id && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.patient_hospital_id}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Age <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.age ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.age && (
                <p className="mt-1 text-sm text-red-500">{errors.age}</p>
              )}
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.date_of_birth ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.date_of_birth && <p className="mt-1 text-sm text-red-500">{errors.date_of_birth}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Age</label>
              <input
                type="text"
                value={age}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
              />
            </div> */}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.gender ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && (
                <p className="mt-1 text-sm text-red-500">{errors.gender}</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg space-y-6">
          <h3 className="text-lg font-semibold text-gray-800">
            Doctor and Device Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Doctor <span className="text-red-500">*</span>
              </label>
              <select
                value={doctorId}
                onChange={(e) => setDoctorId(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.doctor_id ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              >
                <option value="">Select Doctor</option>

                {doctors.map((doctor) => (
                  <option key={doctor.user_id} value={doctor.user_id}>
                    {doctor.name}
                  </option>
                ))}
              </select>
              {errors.doctor_id && (
                <p className="mt-1 text-sm text-red-500">{errors.doctor_id}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="text"
                value={
                  doctorId
                    ? doctors.find((doctor) => doctor.user_id === doctorId)
                        ?.email || ""
                    : ""
                }
                readOnly
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="text"
                value={
                  doctorId
                    ? doctors.find((doctor) => doctor.user_id === doctorId)
                        ?.mobile || ""
                    : ""
                }
                readOnly
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Device <span className="text-red-500">*</span>
              </label>
              <select
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.device_id ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              >
                <option value="">Select Device</option>
                {devices.map((device) => (
                  <option key={device.device_id} value={device.device_id}>
                    {device.device_name}
                  </option>
                ))}
              </select>
              {errors.device_id && (
                <p className="mt-1 text-sm text-red-500">{errors.device_id}</p>
              )}
            </div>
          </div>
        </div>

        {/* <div className="bg-gray-50 p-6 rounded-lg space-y-6">
          <h3 className="text-lg font-semibold text-gray-800">Caretaker Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caretaker Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={careTakerName}
                onChange={(e) => setCareTakerName(e.target.value)}
                required
                className={`w-full px-4 py-2 border capitalize ${errors.caretaker_name ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.caretaker_name && <p className="mt-1 text-sm text-red-500">{errors.caretaker_name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caretaker Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={careTakerEmail}
                onChange={(e) => setCareTakerEmail(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.caretaker_email ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.caretaker_email && <p className="mt-1 text-sm text-red-500">{errors.caretaker_email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caretaker Mobile <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={careTakerMobile}
                onChange={(e) => setCareTakerMobile(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.caretaker_mobile ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.caretaker_mobile && <p className="mt-1 text-sm text-red-500">{errors.caretaker_mobile}</p>}
            </div>

          </div>
        </div> */}

        <div className="bg-gray-50 p-6 rounded-lg space-y-6">
          <h3 className="text-lg font-semibold text-gray-800">Other Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ward Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={wardName}
                onChange={(e) => setWardName(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.ward_name ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.ward_name && (
                <p className="mt-1 text-sm text-red-500">{errors.ward_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bed Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                min="1"
                max="5"
                value={bedNumber}
                onChange={(e) => setBedNumber(e.target.value)}
                required
                className={`w-full px-4 py-2 border ${errors.bed_number ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors.bed_number && (
                <p className="mt-1 text-sm text-red-500">{errors.bed_number}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admitted On
              </label>
              <input
                type="date"
                value={admittedOn ? admittedOn.split("T")[0] : ""}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
              />
            </div>

            {isEditMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={isActive}
                  onChange={(e) => setIsActive(e.target.value === "true")}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-4 rounded-b-lg sticky bottom-0">
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
          className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading
            ? isEditMode
              ? "Saving..."
              : "Adding..."
            : isEditMode
              ? "Save Changes"
              : "Add Patient"}
        </button>
      </div>
    </div>
  );
};

export default OnboardPatientForm;
